import { publicationStateMeta, type PublicationSignal } from "@/lib/listing-pricing";
import {
  automaticDirtyDomains,
  compactOperatorDomainLabels,
} from "@/lib/phase1-sync-contract";

export type ConnectorCoverageInput = {
  id: string;
  name: string;
  provider: string;
  authStatus?: string | null;
};

export type ListingCoverageInput = {
  connectorId: string;
  publicationStatus?: string | null;
  status?: string | null;
  hasDirty?: boolean;
  dirtyDomains?: string[] | null;
  lastError?: string | null;
  externalListingId?: string | null;
  providerConfirmedListed?: boolean;
};

export type ChannelMatrixKind = "listed" | "paused" | "needs_sync" | "not_listed" | "error";

export type ChannelCoverageBadge = {
  connectorId: string;
  name: string;
  provider: string;
  signal: PublicationSignal;
  label: string;
  matrixKind: ChannelMatrixKind;
  matrixLabel: string;
  pendingManualLabels: string[];
  automaticLine: string | null;
  listed: boolean;
  needsAttention: boolean;
  liveExternalId: string | null;
};

function listingDirtyList(listing: ListingCoverageInput): string[] | undefined {
  if (listing.dirtyDomains) return listing.dirtyDomains;
  return undefined;
}

function listingHasAnyDirty(listing: ListingCoverageInput): boolean {
  const dirty = listingDirtyList(listing);
  if (dirty) return dirty.length > 0;
  return Boolean(listing.hasDirty);
}

/** Stable Products-table column order: display name, then connector id. */
export function orderSellingConnectors<T extends ConnectorCoverageInput>(connectors: T[]): T[] {
  return connectors
    .filter((c) => isSellingProvider(c.provider))
    .slice()
    .sort((a, b) => {
      const byName = a.name.localeCompare(b.name, "en");
      return byName !== 0 ? byName : a.id.localeCompare(b.id);
    });
}

function automaticSyncLine(
  automatic: string[],
  failed: boolean,
): string | null {
  const price = automatic.includes("pricing");
  const inventory = automatic.includes("inventory");
  if (!price && !inventory) return null;
  if (failed) {
    if (price && !inventory) return "Price sync failed";
    if (inventory && !price) return "Inventory sync failed";
    return "Price · Inventory sync failed";
  }
  if (price && inventory) return "Price · Inventory sync pending";
  if (price) return "Price sync pending";
  return "Inventory sync pending";
}

export type ChannelMatrixPresentation = {
  kind: ChannelMatrixKind;
  label: string;
  pendingManualLabels: string[];
  automaticLine: string | null;
};

/**
 * One scannable Products-table state from canonical listing/publication only.
 * Does not use product readiness and does not require a provider GET.
 */
export function channelMatrixPresentation(listing: ListingCoverageInput | undefined): ChannelMatrixPresentation {
  const empty: ChannelMatrixPresentation = {
    kind: "not_listed",
    label: "Not listed",
    pendingManualLabels: [],
    automaticLine: null,
  };
  if (!listing) return empty;
  const listed = Boolean(
    listing.providerConfirmedListed ||
      listing.publicationStatus === "published" ||
      listing.externalListingId,
  );
  const dirty = listingDirtyList(listing);
  const automatic = automaticDirtyDomains(dirty ?? []);
  const hasDirty = listingHasAnyDirty(listing);
  const failed =
    listing.publicationStatus === "sync_failed" ||
    listing.status === "error" ||
    Boolean(listing.lastError);
  const autoLine = automaticSyncLine(automatic, failed);
  const manual = compactOperatorDomainLabels(dirty);

  if (listing.status === "archived" || listing.status === "ended") {
    return empty;
  }
  if (listing.status === "paused" && listed) {
    return {
      kind: "paused",
      label: "Paused",
      pendingManualLabels: [],
      automaticLine: autoLine,
    };
  }
  if (!listed) {
    if (listing.status === "publishing") {
      return {
        kind: "needs_sync",
        label: "Publishing…",
        pendingManualLabels: manual,
        automaticLine: autoLine,
      };
    }
    if (failed) {
      return {
        kind: "error",
        label: listing.publicationStatus === "remote_review" ? "Reconciliation required" : "Error / Needs attention",
        pendingManualLabels: manual,
        automaticLine: autoLine,
      };
    }
    return {
      kind: "needs_sync",
      label: "Listing setup",
      pendingManualLabels: manual,
      automaticLine: autoLine,
    };
  }

  if (failed) {
    if (autoLine && manual.length === 0) {
      return {
        kind: "error",
        label: autoLine,
        pendingManualLabels: [],
        automaticLine: null,
      };
    }
    return {
      kind: "error",
      label: "Error / Needs attention",
      pendingManualLabels: manual,
      automaticLine: autoLine,
    };
  }
  if (manual.length > 0) {
    return {
      kind: "needs_sync",
      label: "Needs sync",
      pendingManualLabels: manual,
      automaticLine: autoLine,
    };
  }
  if (autoLine) {
    return {
      kind: "needs_sync",
      label: autoLine,
      pendingManualLabels: [],
      automaticLine: null,
    };
  }
  if (listing.publicationStatus === "drift" || listing.status === "publishing" || (hasDirty && !dirty)) {
    return {
      kind: "needs_sync",
      label: "Needs sync",
      pendingManualLabels: [],
      automaticLine: null,
    };
  }
  return {
    kind: "listed",
    label: "Listed",
    pendingManualLabels: [],
    automaticLine: null,
  };
}

export function channelMatrixKind(listing: ListingCoverageInput | undefined): {
  kind: ChannelMatrixKind;
  label: string;
} {
  const p = channelMatrixPresentation(listing);
  return { kind: p.kind, label: p.label };
}

export function connectorIsReady(authStatus: string | null | undefined): boolean {
  return authStatus === "connected" || authStatus === "authorized";
}

/** Marketplace/storefront connectors — not a hardcoded list of three. */
export function isSellingProvider(provider: string | null | undefined): boolean {
  if (!provider) return false;
  return provider !== "internal" && provider !== "warehouse" && provider !== "manual";
}

export function computeChannelCoverage(
  connectors: ConnectorCoverageInput[],
  listings: ListingCoverageInput[],
): ChannelCoverageBadge[] {
  const selling = orderSellingConnectors(connectors);
  return selling.map((c) => {
    const listing = listings.find((l) => l.connectorId === c.id);
    const ready = connectorIsReady(c.authStatus);
    const meta = publicationStateMeta({
      publicationStatus: (listing?.publicationStatus as import("@/lib/channels").PublicationStatus | undefined) ?? "not_published",
      status: listing?.status as never,
      hasDirty: listing?.hasDirty,
      connectionReady: ready,
      lastError: listing?.lastError,
      providerConfirmedListed: listing?.providerConfirmedListed,
    });
    const trulyListed = Boolean(
      listing?.providerConfirmedListed ||
        listing?.publicationStatus === "published" ||
        listing?.externalListingId,
    );
    const matrix = channelMatrixPresentation(listing);
    return {
      connectorId: c.id,
      name: c.name,
      provider: c.provider,
      signal: listing ? meta.signal : "grey",
      label: trulyListed ? meta.label : matrix.label,
      matrixKind: matrix.kind,
      matrixLabel: matrix.label,
      pendingManualLabels: matrix.pendingManualLabels,
      automaticLine: matrix.automaticLine,
      listed: trulyListed,
      needsAttention: (Boolean(listing) && !trulyListed)
        || (meta.signal === "amber" && listing?.status !== "paused")
        || meta.signal === "red"
        || (!trulyListed && ready),
      liveExternalId: listing?.externalListingId ?? null,
    };
  });
}

export function channelCoverageSummary(badges: ChannelCoverageBadge[]): {
  listedCount: number;
  total: number;
  label: string;
  fullyListed: boolean;
  missingChannel: boolean;
  notListedAnywhere: boolean;
  needsAttention: boolean;
} {
  const total = badges.length;
  const listedCount = badges.filter((b) => b.listed).length;
  const fullyListed = total > 0 && listedCount === total;
  const notListedAnywhere = listedCount === 0;
  const missingChannel = listedCount > 0 && listedCount < total;
  return {
    listedCount,
    total,
    label: total === 0 ? "No selling channels" : fullyListed ? `${listedCount}/${total} listed` : notListedAnywhere ? "Not listed anywhere" : `${listedCount}/${total} listed`,
    fullyListed,
    missingChannel,
    notListedAnywhere,
    needsAttention: badges.some((b) => b.needsAttention && !b.listed) || badges.some((b) => b.signal === "red" || b.signal === "amber"),
  };
}

export type CatalogueChannelFilter =
  | "all"
  | "fully_listed"
  | "missing_channel"
  | "not_listed"
  | "needs_attention"
  | `listed:${string}`
  | `not_listed:${string}`;

export function productMatchesChannelFilter(
  badges: ChannelCoverageBadge[],
  filter: CatalogueChannelFilter,
  extra?: { isDraft?: boolean; lowStock?: boolean; outOfStock?: boolean; missingRequired?: boolean; attentionReasons?: string[] },
): boolean {
  const summary = channelCoverageSummary(badges);
  if (filter === "all") return true;
  if (filter === "fully_listed") return summary.fullyListed;
  if (filter === "missing_channel") return summary.missingChannel;
  if (filter === "not_listed") return summary.notListedAnywhere;
  if (filter === "needs_attention") {
    return Boolean(
      extra?.isDraft ||
        extra?.lowStock ||
        extra?.outOfStock ||
        extra?.missingRequired ||
        (extra?.attentionReasons && extra.attentionReasons.length > 0) ||
        summary.needsAttention,
    );
  }
  if (filter.startsWith("listed:")) {
    const id = filter.slice("listed:".length);
    return badges.some((b) => b.connectorId === id && b.listed);
  }
  if (filter.startsWith("not_listed:")) {
    const id = filter.slice("not_listed:".length);
    return badges.some((b) => b.connectorId === id && !b.listed);
  }
  return true;
}
